import {
  type Account,
  type Chain,
  type Client,
  type Hash,
  InvalidParamsRpcError,
  type Transport,
  type TransactionRequestEIP1559,
  type WalletClient
} from 'viem'
import { sendTransaction } from 'viem/actions'

type SendTransactionParameters = Parameters<WalletClient['sendTransaction']>[0]
type WalletTransactionParameters = SendTransactionParameters | TransactionRequestEIP1559

interface SendTxRpcParams {
  method: string
  params?: Array<Record<string, unknown>>
}

/**
 * Some wallets (Backpack via ethers v6) only read `gasLimit` and drop `gas`, then
 * re-estimate without the app's buffer. Forward the buffered value under both names;
 * a wallet that rejects the extra key gets one retry with the original payload,
 * but only on invalid params (-32602): other codes, including -32000, can arrive
 * after the wallet prompt, and retrying them would prompt again. This wrapper
 * skips its own retry once the submit is stale; viem's eth_ -> wallet_ namespace
 * fallback is not gated and still sends the aliased payload.
 */
export async function sendWalletTransaction(
  client: Client,
  parameters: WalletTransactionParameters,
  isStaleSubmit?: () => boolean
): Promise<Hash> {
  if (parameters.gas === undefined)
    return sendTransaction(client, parameters as SendTransactionParameters)

  const request = (async (args: SendTxRpcParams, options?: unknown) => {
    const tx = args.params?.[0]
    const isSend = args.method === 'eth_sendTransaction' || args.method === 'wallet_sendTransaction'
    if (!isSend || tx?.gas === undefined || tx.gasLimit !== undefined)
      return client.request(args as never, options as never)
    try {
      return await client.request(
        { ...args, params: [{ ...tx, gasLimit: tx.gas }] } as never,
        options as never
      )
    } catch (error) {
      if ((error as { code?: number }).code === InvalidParamsRpcError.code && !isStaleSubmit?.())
        return client.request(args as never, options as never)
      throw error
    }
  }) as Client<Transport, Chain, Account>['request']

  // This only works because viem's json-rpc send path calls `client.request` directly;
  // the real-WalletClient test catches a viem upgrade that moves it.
  return sendTransaction({ ...client, request }, parameters as SendTransactionParameters)
}
